// ===================================================================
// Intake -- Reports & Analytics
// Stats, charts, CSV/PDF export, AI narrative
// Ported from Reports.jsx
// ===================================================================

import { useEffect, useMemo, useState } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, Stack, SimpleGrid,
  Table, TextInput, Center, ThemeIcon, Box, Divider, Loader,
  Textarea,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertTriangle, IconDownload, IconFileTypePdf,
  IconSparkles, IconSearch, IconSortAscending, IconSortDescending,
} from '@tabler/icons-react';
import { useIntakeStore } from '../../store/intakeStore';
import { useAuthStore } from '../../store/authStore';
import { callClaude } from '../../lib/api-switch';
import { formatDateTime, TRIAGE_COLORS } from './lib/helpers';
import Papa from 'papaparse';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { primary, semantic } from '../../lib/colors';

// ── Inline bar chart component ───────────────────────────────────
function BarChart({ data }: { data: Array<{ label: string; value: number; color: string }> }) {
  const max = Math.max(...data.map(d => d.value), 1);
  return (
    <Stack gap="xs">
      {data.map(d => (
        <Group key={d.label} gap="xs" wrap="nowrap">
          <Text size="xs" w={100} ta="right" style={{ flexShrink: 0 }}>{d.label}</Text>
          <Box style={{ flex: 1, position: 'relative', height: 22, borderRadius: 4, background: 'var(--mantine-color-gray-1)' }}>
            <Box
              style={{
                position: 'absolute', left: 0, top: 0, bottom: 0,
                width: `${(d.value / max) * 100}%`,
                background: d.color,
                borderRadius: 4,
                minWidth: d.value > 0 ? 4 : 0,
                transition: 'width 0.3s ease',
              }}
            />
            <Text
              size="xs"
              fw={600}
              style={{
                position: 'absolute', left: 8, top: 2,
                color: (d.value / max) > 0.3 ? '#fff' : '#333',
              }}
            >
              {d.value}
            </Text>
          </Box>
        </Group>
      ))}
    </Stack>
  );
}

type SortKey = 'lastName' | 'triage' | 'status' | 'datecreated';

export default function ReportsPage() {
  const { individuals, discharges, loadIndividuals, loadDischarges } = useIntakeStore();
  const { activeIncident } = useAuthStore();

  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('datecreated');
  const [sortDesc, setSortDesc] = useState(true);
  const [aiNarrative, setAiNarrative] = useState('');
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    if (!activeIncident?.id) return;
    loadIndividuals(String(activeIncident.id));
    loadDischarges(String(activeIncident.id));
  }, [activeIncident?.id, loadIndividuals, loadDischarges]);

  // ── Computed stats ─────────────────────────────────────────────
  const incidentIndividuals = useMemo(() => {
    if (!activeIncident) return [];
    return individuals.filter(i => String(i.incidentId) === String(activeIncident.id));
  }, [individuals, activeIncident]);

  const incidentDischarges = useMemo(() => {
    if (!activeIncident) return [];
    return discharges.filter(d => String(d.incidentId) === String(activeIncident.id));
  }, [discharges, activeIncident]);

  const stats = useMemo(() => {
    const total = incidentIndividuals.length;
    const active = incidentIndividuals.filter(i => i.status !== 'discharged').length;
    const discharged = incidentIndividuals.filter(i => i.status === 'discharged').length;
    let red = 0, yellow = 0, green = 0;
    for (const i of incidentIndividuals) {
      const t = (i.triage || 'green').toLowerCase();
      if (t === 'red') red++;
      else if (t === 'yellow') yellow++;
      else green++;
    }
    return { total, active, discharged, red, yellow, green };
  }, [incidentIndividuals]);

  // Intake method breakdown
  const methodCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    incidentIndividuals.forEach(i => {
      const m = i.intakeMethod || 'manual';
      counts[m] = (counts[m] || 0) + 1;
    });
    return Object.entries(counts).map(([label, value]) => ({
      label: label.charAt(0).toUpperCase() + label.slice(1),
      value,
      color: primary[700],
    }));
  }, [incidentIndividuals]);

  // Discharge destination breakdown
  const destCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    incidentDischarges.forEach(d => {
      const dest = d.destination || 'other';
      counts[dest] = (counts[dest] || 0) + 1;
    });
    return Object.entries(counts).map(([label, value]) => ({
      label: label.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      value,
      color: '#07963F',
    }));
  }, [incidentDischarges]);

  // Recent table with sorting/filtering
  const tableRows = useMemo(() => {
    let list = [...incidentIndividuals];
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(i =>
        i.firstName.toLowerCase().includes(q) ||
        i.lastName.toLowerCase().includes(q) ||
        i.tagCode.toLowerCase().includes(q)
      );
    }
    list.sort((a, b) => {
      const aVal = (a[sortKey] ?? '').toString().toLowerCase();
      const bVal = (b[sortKey] ?? '').toString().toLowerCase();
      const cmp = aVal.localeCompare(bVal);
      return sortDesc ? -cmp : cmp;
    });
    return list.slice(0, 50);
  }, [incidentIndividuals, search, sortKey, sortDesc]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDesc(!sortDesc);
    else { setSortKey(key); setSortDesc(false); }
  };

  const SortIcon = sortDesc ? IconSortDescending : IconSortAscending;

  // ── CSV Export ─────────────────────────────────────────────────
  const handleExportCSV = () => {
    const rows = incidentIndividuals.map(i => ({
      'Tag Code': i.tagCode,
      'Last Name': i.lastName,
      'First Name': i.firstName,
      'DOB': i.dob,
      'Gender': i.gender,
      'Triage': i.triage || 'green',
      'Status': i.status || 'active',
      'Intake Method': i.intakeMethod || 'manual',
      'Checked In': i.checkedInAt || i.datecreated || '',
      'Medical Needs': i.medicalNeeds || '',
      'Medications': i.medications || '',
      'Emergency Contact': i.emergencyContact || '',
      'Contact Phone': i.contactPhone || '',
      'Notes': i.notes || '',
    }));

    const csv = Papa.unparse(rows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `intake-roster-${activeIncident?.name?.replace(/\s+/g, '-') || 'export'}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    notifications.show({ title: 'Exported', message: `${rows.length} records exported to CSV.`, color: 'green' });
  };

  // ── PDF Export ─────────────────────────────────────────────────
  const handleExportPDF = () => {
    const doc = new jsPDF();
    const incName = activeIncident?.name || 'Incident';
    const now = new Date().toLocaleString();

    // Header
    doc.setFontSize(18);
    doc.setTextColor(44, 1, 214); // Navy brand
    doc.text('OpenGov EMC', 14, 20);
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text(`Intake Report: ${incName}`, 14, 30);
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Generated: ${now}`, 14, 37);

    // Stats boxes
    let y = 45;
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.text(`Total Registered: ${stats.total}`, 14, y);
    doc.text(`Still On-Scene: ${stats.active}`, 80, y);
    doc.text(`Discharged: ${stats.discharged}`, 140, y);
    y += 7;
    doc.text(`Red (Immediate): ${stats.red}`, 14, y);
    doc.text(`Yellow (Delayed): ${stats.yellow}`, 80, y);
    doc.text(`Green (Walking): ${stats.green}`, 140, y);
    y += 10;

    // AI summary if available
    if (aiNarrative) {
      doc.setFontSize(11);
      doc.setTextColor(44, 1, 214);
      doc.text('AI Narrative Summary:', 14, y);
      y += 6;
      doc.setFontSize(9);
      doc.setTextColor(0, 0, 0);
      const lines = doc.splitTextToSize(aiNarrative, 180);
      doc.text(lines, 14, y);
      y += lines.length * 4.5 + 5;
    }

    // Roster table
    const tableData = incidentIndividuals.map(i => [
      i.tagCode,
      `${i.lastName}, ${i.firstName}`,
      i.dob || '',
      (i.triage || 'green').toUpperCase(),
      i.status || 'active',
      i.intakeMethod || 'manual',
      i.checkedInAt ? new Date(i.checkedInAt).toLocaleDateString() : '',
    ]);

    autoTable(doc, {
      startY: y,
      head: [['Tag', 'Name', 'DOB', 'Triage', 'Status', 'Method', 'Checked In']],
      body: tableData,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [44, 1, 214] },
    });

    doc.save(`intake-report-${incName.replace(/\s+/g, '-')}-${new Date().toISOString().slice(0, 10)}.pdf`);
    notifications.show({ title: 'PDF Generated', message: 'Report saved to PDF.', color: 'green' });
  };

  // ── AI Narrative ───────────────────────────────────────────────
  const handleGenerateNarrative = async () => {
    setAiLoading(true);
    try {
      const prompt = `You are an emergency management specialist. Generate a brief SITREP narrative for the following intake data at an emergency operations center.

Incident: ${activeIncident?.name || 'Unknown'}
Type: ${activeIncident?.incidentType || 'Unknown'}

Current Stats:
- Total Registered: ${stats.total}
- Still On-Scene: ${stats.active}
- Discharged: ${stats.discharged}
- Triage Red (Immediate): ${stats.red}
- Triage Yellow (Delayed): ${stats.yellow}
- Triage Green (Walking): ${stats.green}

Intake Methods: ${methodCounts.map(m => `${m.label}: ${m.value}`).join(', ') || 'None'}
Discharge Destinations: ${destCounts.map(d => `${d.label}: ${d.value}`).join(', ') || 'None'}

Write a 2-3 paragraph SITREP narrative summarizing the current intake status, notable patterns, and operational recommendations. Use professional ICS language.`;

      const result = await callClaude(
        [{ role: 'user', content: prompt }],
        { maxTokens: 1024, system: 'You are a professional emergency management report writer.' }
      );
      setAiNarrative(result);
    } catch (err) {
      notifications.show({
        title: 'AI Error',
        message: err instanceof Error ? err.message : 'Failed to generate narrative.',
        color: 'red',
      });
    } finally {
      setAiLoading(false);
    }
  };

  // ── No incident guard ──────────────────────────────────────────
  if (!activeIncident) {
    return (
      <Center py={80}>
        <Stack align="center" gap="sm">
          <ThemeIcon size={60} radius="xl" variant="light" color="gray"><IconAlertTriangle size={30} /></ThemeIcon>
          <Title order={4}>No Incident Selected</Title>
          <Text size="sm" c="dimmed">Use the incident selector in the top header bar.</Text>
        </Stack>
      </Center>
    );
  }

  return (
    <div>
      {/* Header */}
      <Group justify="space-between" mb="md">
        <Group gap="xs">
          <Title order={3} c="black">Reports</Title>
        </Group>
        <Group gap="xs">
          <Button
            variant="light"
            size="sm"
            leftSection={<IconDownload size={14} />}
            onClick={handleExportCSV}
          >
            Export CSV
          </Button>
          <Button
            variant="light"
            size="sm"
            leftSection={<IconFileTypePdf size={14} />}
            onClick={handleExportPDF}
          >
            Export PDF
          </Button>
        </Group>
      </Group>

      {/* Stat Cards */}
      <SimpleGrid cols={{ base: 2, sm: 3, md: 5 }} mb="md">
        {[
          { label: 'Total Registered', value: stats.total, color: primary[700] },
          { label: 'Still On-Scene', value: stats.active, color: '#07963F' },
          { label: 'Discharged', value: stats.discharged, color: '#A67103' },
          { label: 'Red (Immediate)', value: stats.red, color: semantic.error },
          { label: 'Yellow (Delayed)', value: stats.yellow, color: '#d97706' },
        ].map(s => (
          <Paper key={s.label} p="md" radius="sm" withBorder>
            <Text fw={700} fz={28} style={{ color: s.color }}>{s.value}</Text>
            <Text size="xs" fw={600}>{s.label}</Text>
          </Paper>
        ))}
      </SimpleGrid>

      {/* Charts Row */}
      <SimpleGrid cols={{ base: 1, md: 2 }} mb="md">
        {/* Triage Breakdown */}
        <Paper p="md" radius="sm" withBorder>
          <Text fw={600} mb="sm">Triage Breakdown</Text>
          <BarChart data={[
            { label: 'Green', value: stats.green, color: TRIAGE_COLORS.green },
            { label: 'Yellow', value: stats.yellow, color: TRIAGE_COLORS.yellow },
            { label: 'Red', value: stats.red, color: TRIAGE_COLORS.red },
          ]} />
        </Paper>

        {/* Intake Methods + Discharge Destinations */}
        <Paper p="md" radius="sm" withBorder>
          <Text fw={600} mb="sm">Intake Methods</Text>
          {methodCounts.length > 0 ? (
            <BarChart data={methodCounts} />
          ) : (
            <Text size="sm" c="dimmed">No data yet.</Text>
          )}

          <Divider my="sm" />

          <Text fw={600} mb="sm">Discharge Destinations</Text>
          {destCounts.length > 0 ? (
            <BarChart data={destCounts} />
          ) : (
            <Text size="sm" c="dimmed">No discharges yet.</Text>
          )}
        </Paper>
      </SimpleGrid>

      {/* AI Narrative */}
      <Paper p="md" radius="sm" withBorder mb="md">
        <Group justify="space-between" mb="sm">
          <Text fw={600}>AI Narrative (SITREP)</Text>
          <Button
            variant="light"
            size="sm"
            leftSection={aiLoading ? <Loader size={14} /> : <IconSparkles size={14} />}
            onClick={handleGenerateNarrative}
            loading={aiLoading}
          >
            Generate Narrative
          </Button>
        </Group>
        {aiNarrative ? (
          <Textarea
            value={aiNarrative}
            onChange={e => setAiNarrative(e.currentTarget.value)}
            minRows={6}
            autosize
            styles={{ input: { fontFamily: 'var(--mantine-font-family)' } }}
          />
        ) : (
          <Text size="sm" c="dimmed">
            Click "Generate Narrative" to create an AI-written SITREP summary of current intake data.
          </Text>
        )}
      </Paper>

      {/* Recent Registrations Table */}
      <Paper p="md" radius="sm" withBorder>
        <Group justify="space-between" mb="sm">
          <Text fw={600}>Recent Registrations</Text>
          <TextInput
            placeholder="Search..."
            leftSection={<IconSearch size={14} />}
            value={search}
            onChange={e => setSearch(e.currentTarget.value)}
            size="xs"
            w={200}
          />
        </Group>

        <Box style={{ overflow: 'auto', maxHeight: 500 }}>
          <Table highlightOnHover striped stickyHeader>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Tag</Table.Th>
                <Table.Th style={{ cursor: 'pointer' }} onClick={() => handleSort('lastName')}>
                  <Group gap={4}>Name {sortKey === 'lastName' && <SortIcon size={12} />}</Group>
                </Table.Th>
                <Table.Th>DOB</Table.Th>
                <Table.Th style={{ cursor: 'pointer' }} onClick={() => handleSort('triage')}>
                  <Group gap={4}>Triage {sortKey === 'triage' && <SortIcon size={12} />}</Group>
                </Table.Th>
                <Table.Th style={{ cursor: 'pointer' }} onClick={() => handleSort('status')}>
                  <Group gap={4}>Status {sortKey === 'status' && <SortIcon size={12} />}</Group>
                </Table.Th>
                <Table.Th style={{ cursor: 'pointer' }} onClick={() => handleSort('datecreated')}>
                  <Group gap={4}>Registered {sortKey === 'datecreated' && <SortIcon size={12} />}</Group>
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {tableRows.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <Text ta="center" c="dimmed" py="lg">No registrations found.</Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                tableRows.map(ind => (
                  <Table.Tr key={ind.id}>
                    <Table.Td>
                      <Badge size="sm" variant="outline" color="gray" ff="monospace">{ind.tagCode}</Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" fw={500}>{ind.lastName}, {ind.firstName}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{ind.dob || '\u2014'}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        size="sm"
                        color={ind.triage === 'red' ? 'red' : ind.triage === 'yellow' ? 'yellow' : 'green'}
                        variant="filled"
                      >
                        {(ind.triage || 'green').toUpperCase()}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        size="sm"
                        variant="light"
                        color={ind.status === 'discharged' ? 'gray' : 'blue'}
                      >
                        {ind.status || 'active'}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">{formatDateTime(ind.datecreated)}</Text>
                    </Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        </Box>
        {incidentIndividuals.length > 50 && (
          <Text size="xs" c="dimmed" ta="center" mt="xs">
            Showing 50 of {incidentIndividuals.length} registrations. Export CSV for full data.
          </Text>
        )}
      </Paper>
    </div>
  );
}
